import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UserButton, useUser } from "@clerk/clerk-react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box, Drawer, List, Typography, IconButton, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Badge, Divider, Paper, Stack, Tooltip,
  InputBase, useTheme, useMediaQuery, Chip, alpha, Collapse
} from "@mui/material";
import {
  DashboardOutlined as DashboardIcon,
  TheaterComedyOutlined as TheaterIcon,
  CalendarMonthOutlined as CalendarIcon,
  LocalActivityOutlined as BookingIcon,
  SettingsOutlined as SettingsIcon,
  QrCodeScannerOutlined as ScannerIcon,
  MenuRounded as MenuMobileIcon,
  SearchRounded as SearchIcon,
  NotificationsNoneOutlined as BellIcon,
  DarkModeOutlined as DarkIcon,
  LightModeOutlined as LightIcon,
  KeyboardDoubleArrowLeft as DoubleChevronLeftIcon,
  KeyboardDoubleArrowRight as DoubleChevronIcon,
  ChairAltOutlined as ChairIcon,
  HomeOutlined as HomeIcon,
  CreditCardOutlined as CreditCardIcon,
  BarChart as AnalyticsIcon,
  ChevronRight,
  ExpandMore,
  Circle as DotIcon,
  GridViewOutlined as LayoutIcon,
  ShieldOutlined as ShieldIcon,
  DescriptionOutlined as PagesIcon,
  PersonOutline as ProfileIcon,
  HelpOutline as FAQIcon,
  AttachMoney as PricingIcon
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useAdminTheme } from "../admin/AdminThemeProvider";
import { useSocket } from "../../contexts/SocketContext";
import { getPartnerProfile } from "../../store/slices/partnerSlice";
import ModernLoader from "../../components/common/ModernLoader";
import BrandLogo from "../common/BrandLogo";

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 80;

const PartnerLayout = ({ children }) => {
  const { mode, toggleTheme } = useAdminTheme();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: clerkUser, isLoaded: clerkLoaded, isSignedIn } = useUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { partner, loading, profileChecked } = useSelector(state => state.partner);
  const dispatch = useDispatch();
  const { socket: socketInstance } = useSocket();

  const [isPinned, setIsPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const initialFetchDone = useRef(false);

  const isExpanded = isPinned || hovered;

  // 1. Dedicated Initial Profile Fetch (Strict Once-Only)
  useEffect(() => {
    if (clerkLoaded && isSignedIn && clerkUser?.id && !initialFetchDone.current) {
      initialFetchDone.current = true;
      dispatch(getPartnerProfile(clerkUser.id)).then((action) => {
        if (action.payload?.partner?.theaters?.length > 0 && !localStorage.getItem('activeTheaterId')) {
          localStorage.setItem('activeTheaterId', action.payload.partner.theaters[0]._id || action.payload.partner.theaters[0]);
        }
      });
    }
  }, [clerkLoaded, isSignedIn, clerkUser?.id, dispatch]);

  // 2. Real-time updates & Scroll Logic
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);

    const handleUpdate = (updatedPartner) => {
      if (clerkUser?.id === updatedPartner.userId && !loading) {
        dispatch(getPartnerProfile(clerkUser.id));
      }
    };

    if (socketInstance) {
      socketInstance.on("partner-updated", handleUpdate);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (socketInstance) socketInstance.off("partner-updated", handleUpdate);
    };
  }, [socketInstance, clerkUser?.id, loading, dispatch]);

  const togglePin = () => setIsPinned(!isPinned);
  const toggleMobileSidebar = () => setMobileOpen(!mobileOpen);

  const toggleSubmenu = (menu) => {
    if (!isExpanded) return;
    setOpenSubmenus(prev => ({ ...prev, [menu]: !prev[menu] }));
  };

  const navItems = [
    { type: "header", label: "General" },
    { label: "Dashboard", icon: <DashboardIcon />, path: "/partner" },
    { label: "Roles & Permissions", icon: <ShieldIcon />, path: "/partner/roles" },
    { label: "Pages", icon: <PagesIcon />, path: "/partner/pages" },
    { label: "User Profile", icon: <ProfileIcon />, path: "/partner/profile" },
    {
      label: "Account Settings",
      icon: <SettingsIcon />,
      type: "submenu",
      children: [
        { label: "Account", path: "/partner/settings?tab=0" },
        { label: "Security", path: "/partner/settings?tab=1" },
        { label: "Billing & Plans", path: "/partner/settings?tab=2" },
        { label: "Notifications", path: "/partner/settings?tab=3" },
        { label: "Connections", path: "/partner/settings?tab=4" },
      ]
    },
    { type: "header", label: "Properties" },
    { label: "My Theaters", icon: <TheaterIcon />, path: "/partner/theaters" },
    { label: "Cinema Screens", icon: <ChairIcon />, path: "/partner/screens" },
    { label: "Show Schedule", icon: <CalendarIcon />, path: "/partner/shows" },
    { type: "header", label: "Management" },
    { label: "Ticket Bookings", icon: <BookingIcon />, path: "/partner/bookings" },
    { label: "Entry Scanner", icon: <ScannerIcon />, path: "/partner/scanner" },
    { type: "header", label: "Community" },
    { label: "FAQ", icon: <FAQIcon />, path: "/partner/faq" },
    { label: "Pricing", icon: <PricingIcon />, path: "/partner/subscription" },
    { label: "Back to Portal", icon: <HomeIcon />, path: "/" },
  ];

  if (clerkLoaded && isSignedIn && !profileChecked) {
    return <ModernLoader text="Authenticating Cinema Protocol..." />;
  }

  if (!clerkLoaded || !isSignedIn) {
    return <ModernLoader text="Synchronizing Identity Protocol..." />;
  }

  const statusColors = {
    APPROVED: '#56ca00',
    UNDER_REVIEW: '#ffb400',
    PENDING_SUBMISSION: '#8a8d93',
    REJECTED: '#ff4c51'
  };

  const drawerContent = (
    <Box
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        position: "relative",
        boxShadow: hovered && !isPinned ? (mode === "dark" ? "0 4px 18px 0 rgba(0,0,0, 0.4)" : "0 4px 18px 0 rgba(76, 78, 100, 0.15)") : "none",
        transition: "all 0.2s ease",
        width: isExpanded ? DRAWER_WIDTH : COLLAPSED_WIDTH,
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Brand Header */}
      <Box sx={{
        p: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "76px",
        px: isExpanded ? 3 : 0,
        transition: "all 0.2s ease"
      }}>
        <BrandLogo className={!isExpanded ? "collapsed-logo" : ""} />
        {isExpanded && (
          <IconButton onClick={togglePin} sx={{ ml: "auto", p: 0.5 }}>
            {isPinned ? <DoubleChevronLeftIcon sx={{ fontSize: 18, color: theme.palette.text.secondary, opacity: 0.5 }} /> : <DoubleChevronIcon sx={{ fontSize: 18, color: theme.palette.text.secondary, opacity: 0.5 }} />}
          </IconButton>
        )}
      </Box>

      {/* KYC Status Badge - Integrated into Sidebar */}
      {isExpanded && partner && (
        <Box sx={{ px: 2.5, mb: 1 }}>
          <Box sx={{
            p: 1.5, borderRadius: "12px",
            bgcolor: alpha(theme.palette.text.primary, 0.03),
            border: `1px solid ${theme.palette.divider}`,
          }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: statusColors[partner.kycStatus] || '#8a8d93', boxShadow: `0 0 0 2px ${alpha(statusColors[partner.kycStatus] || '#8a8d93', 0.2)}` }} />
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: theme.palette.text.secondary }}>
                {partner.kycStatus?.replace(/_/g, ' ')}
              </Typography>
            </Stack>
            <Chip
              label={partner.currentPlan || 'STARTER'}
              size="small"
              sx={{
                mt: 1, height: 20, fontSize: "0.6rem", fontWeight: 900,
                bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main,
                borderRadius: "6px"
              }}
            />
          </Box>
        </Box>
      )}

      {/* Menu Inner */}
      <Box sx={{
        flexGrow: 1, overflowY: "auto", px: isExpanded ? 1.5 : 1, pb: 4,
        "&::-webkit-scrollbar": { width: "0px" },
      }}>
        <List sx={{ pt: 1 }}>
          {navItems.map((item, index) => {
            if (item.type === "header") {
              return isExpanded && (
                <Typography key={index} sx={{
                  color: theme.palette.text.secondary,
                  opacity: 0.4,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: "1.2px",
                  px: 2.5,
                  mb: 1.5,
                  mt: index > 0 ? 3 : 1,
                  textTransform: "uppercase",
                  whiteSpace: "nowrap"
                }}>
                  {item.label}
                </Typography>
              );
            }

            const isSubmenu = item.type === "submenu";
            const isActive = location.pathname === item.path || (isSubmenu && item.children.some(c => location.pathname === c.path));
            const isSubmenuOpen = openSubmenus[item.label] || (isActive && isExpanded);

            if (isSubmenu) {
              return (
                <Box key={index} sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => toggleSubmenu(item.label)}
                    sx={{
                      mx: isExpanded ? 1 : 0.5,
                      borderRadius: "10px", py: 1.2, px: isExpanded ? 2 : 0,
                      justifyContent: isExpanded ? "flex-start" : "center",
                      bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                      color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                      transition: "0.2s all ease",
                      "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.05) }
                    }}
                  >
                    <ListItemIcon sx={{ color: isActive ? theme.palette.primary.main : "inherit", opacity: isActive ? 1 : 0.7, minWidth: isExpanded ? 38 : "auto", justifyContent: "center" }}>
                      {React.cloneElement(item.icon, { sx: { fontSize: 22 } })}
                    </ListItemIcon>
                    {isExpanded && <ListItemText primary={item.label} primaryTypographyProps={{ sx: { fontWeight: isActive ? 600 : 500, fontSize: "14.5px", color: "inherit", whiteSpace: "nowrap" } }} />}
                    {isExpanded && (isSubmenuOpen ? <ExpandMore sx={{ fontSize: 16, opacity: 0.5, ml: "auto" }} /> : <ChevronRight sx={{ fontSize: 16, opacity: 0.5, ml: "auto" }} />)}
                  </ListItemButton>
                  <Collapse in={isSubmenuOpen && isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ mt: 0.5, mb: 1 }}>
                      {item.children.map((child, idx) => {
                        const isChildActive = location.pathname === child.path;
                        return (
                          <ListItemButton
                            key={idx}
                            onClick={() => navigate(child.path)}
                            sx={{
                              pl: 5.5, py: 1, borderRadius: "10px", mb: 0.5, mx: 1.5,
                              color: isChildActive ? theme.palette.primary.main : theme.palette.text.secondary,
                              bgcolor: isChildActive ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                              "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.05) },
                              transition: "0.2s all"
                            }}
                          >
                            <DotIcon sx={{ fontSize: 7, mr: 2.2, opacity: isChildActive ? 1 : 0.3, color: isChildActive ? theme.palette.primary.main : "inherit" }} />
                            <ListItemText primary={child.label} primaryTypographyProps={{ sx: { fontWeight: isChildActive ? 600 : 400, fontSize: "14px", whiteSpace: "nowrap" } }} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              );
            }

            return (
              <ListItem key={index} disablePadding sx={{ mb: 0.5, px: isExpanded ? 1 : 0.5 }}>
                <ListItemButton
                  onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                  sx={{
                    borderRadius: "10px", py: 1.2, px: isExpanded ? 2 : 0,
                    justifyContent: isExpanded ? "flex-start" : "center",
                    bgcolor: isActive ? theme.palette.primary.main : "transparent",
                    color: isActive ? "#ffffff" : theme.palette.text.secondary,
                    boxShadow: isActive ? `0 6px 12px -4px ${alpha(theme.palette.primary.main, 0.5)}` : "none",
                    transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": { bgcolor: isActive ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.05) }
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit", opacity: isActive ? 1 : 0.7, minWidth: isExpanded ? 38 : "auto", justifyContent: "center" }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 22 } })}
                  </ListItemIcon>
                  {isExpanded && <ListItemText primary={item.label} primaryTypographyProps={{ sx: { fontWeight: isActive ? 600 : 500, fontSize: "14.5px", whiteSpace: "nowrap" } }} />}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", bgcolor: theme.palette.background.default, minHeight: "100vh", transition: "background-color 0.3s ease", maxWidth: "100vw", overflowX: "hidden" }}>
      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={toggleMobileSidebar}
        sx={{
          width: isPinned ? DRAWER_WIDTH : COLLAPSED_WIDTH,
          flexShrink: 0,
          transition: "0.2s all",
          "& .MuiDrawer-paper": {
            width: isExpanded ? DRAWER_WIDTH : COLLAPSED_WIDTH,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: "border-box",
            borderRight: "none",
            boxShadow: isExpanded && !isPinned ? (mode === "dark" ? "0 4px 18px 0 rgba(0,0,0, 0.4)" : "0 4px 18px 0 rgba(76, 78, 100, 0.15)") : (mode === "dark" ? "0 2px 10px 0 rgba(0,0,0, 0.2)" : "0 2px 10px 0 rgba(76, 78, 100, 0.05)"),
            overflowX: "hidden",
            bgcolor: theme.palette.background.paper
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Global Body Framework */}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, width: { md: `calc(100% - ${isPinned ? DRAWER_WIDTH : COLLAPSED_WIDTH}px)` }, transition: "0.2s all", overflowX: "hidden" }}>

        {/* Top Navbar */}
        <Box sx={{
          px: isScrolled ? { xs: 2.5, md: 4.5 } : 0,
          pt: 0,
          position: "sticky",
          top: 0,
          zIndex: 110,
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}>
          <Paper
            elevation={0}
            sx={{
              borderBottomLeftRadius: isScrolled ? "12px" : 0,
              borderBottomRightRadius: isScrolled ? "12px" : 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              bgcolor: isScrolled ? alpha(theme.palette.background.paper, 0.92) : "transparent",
              backdropFilter: isScrolled ? "blur(12px)" : "none",
              boxShadow: isScrolled ? (mode === "dark" ? "0 4px 18px 0 rgba(0,0,0, 0.3)" : "0 4px 18px 0 rgba(76, 78, 100, 0.12)") : "none",
              height: "64px",
              display: "flex",
              alignItems: "center",
              px: { xs: 2, md: 4 },
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              borderBottom: isScrolled ? "none" : `1px solid ${theme.palette.divider}`
            }}
          >
            <IconButton onClick={toggleMobileSidebar} sx={{ mr: 1, color: theme.palette.text.secondary, opacity: 0.68, display: { md: "none" } }}>
              <MenuMobileIcon sx={{ fontSize: 24 }} />
            </IconButton>

            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <SearchIcon sx={{ color: theme.palette.text.secondary, opacity: 0.54, fontSize: 22, mr: 1.5 }} />
              <InputBase placeholder="Search Cinema Hub..." sx={{ fontWeight: 400, fontSize: "15px", width: "100%", color: theme.palette.text.primary, opacity: 0.68 }} />
            </Box>

            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: "auto" }}>
              <Tooltip title={`Switch to ${mode === 'dark' ? 'Light' : 'Dark'} Mode`}>
                <IconButton onClick={toggleTheme} sx={{ p: 1.2, bgcolor: alpha(theme.palette.primary.main, 0.05), color: theme.palette.primary.main, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}>
                  {mode === "dark" ? <LightIcon /> : <DarkIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Layout Shortcuts">
                <IconButton sx={{ color: theme.palette.text.secondary, opacity: 0.68, display: { xs: "none", sm: "inline-flex" } }}>
                  <LayoutIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Notifications">
                <IconButton sx={{ color: theme.palette.text.secondary, opacity: 0.68 }}>
                  <Badge badgeContent={3} color="error" sx={{ "& .MuiBadge-badge": { fontWeight: 600, fontSize: "11px", minWidth: "18px", height: "18px" } }}>
                    <BellIcon sx={{ fontSize: 22 }} />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 1.5, my: 1.5, borderColor: theme.palette.divider }} />

              <Box sx={{ pl: 1, display: "flex", alignItems: "center" }}>
                <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot" sx={{ '& .MuiBadge-badge': { backgroundColor: '#56ca00', color: '#56ca00', boxShadow: `0 0 0 2px ${theme.palette.background.paper}` } }}>
                  <UserButton afterSignOutUrl="/" appearance={{ elements: { avatarBox: { width: 38, height: 38, borderRadius: "50%" } } }} />
                </Badge>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {/* Page Content */}
        <Box sx={{
          p: location.pathname === '/partner/scanner' ? 0 : { xs: 2, md: 4 },
          pt: location.pathname === '/partner/scanner' ? 0 : 3,
          flexGrow: 1
        }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>
    </Box>
  );
};

export default PartnerLayout;
